import type { PilotAttemptRecord, PilotQuestionInstance } from '../pilotMath/types';
import type { LearningDuration } from '../../types/learning';
import type { InkDocumentV1 } from '../../types/ink';

export type TrustedResponseV1 =
  | { type: 'integer'; value: number }
  | { type: 'text'; value: string }
  | { type: 'choice'; optionId: string };

export type TrustedAttemptSubmissionV1 = {
  schemaVersion: 1;
  clientSubmissionId: string;
  studentRef: string;
  assignmentRef: string;
  questionRef: string;
  skillCode: string;
  response: TrustedResponseV1;
  firstResponse?: TrustedResponseV1;
  correctionCount: number;
  hintCount: number;
  timing: LearningDuration;
  startedAt: string;
  completedAt: string;
  ink?: {
    present: true;
    formatVersion: 1;
    strokeCount: number;
    pointCount: number;
  };
};


export type TrustedOutboxEnvelopeV1 = {
  schemaVersion: 1;
  submission: TrustedAttemptSubmissionV1;
  inkDocument?: InkDocumentV1;
};

export type TrustedGradeResultV1 = {
  schemaVersion: 1;
  clientSubmissionId: string;
  status: 'accepted' | 'duplicate' | 'rejected' | 'review_required';
  serverGrade?: {
    correct: boolean;
    graderVersion: string;
  };
  artifacts?: {
    attemptAnswerId?: string | null;
  };
  learnerUpdate?: {
    skillCode: string;
    masteryPercent: number;
    masteryState: string;
    evidenceCount: number;
  };
  message?: string;
};

function countInkPoints(attempt: PilotAttemptRecord) {
  if (!attempt.ink) return 0;
  return attempt.ink.strokes.reduce((sum, stroke) => sum + stroke.points.length, 0);
}

/**
 * Builds the payload allowed to leave the learner device.
 * Intentionally excludes the local correct answer, local mastery result,
 * evidence weight, and local misconception diagnosis. Those are recomputed
 * by the trusted runtime from the frozen assignment/question manifest.
 */
export function buildTrustedAttemptSubmission(input: {
  studentRef: string;
  assignmentRef: string;
  question: PilotQuestionInstance;
  attempt: PilotAttemptRecord;
}): TrustedAttemptSubmissionV1 {
  const { attempt, question } = input;
  return {
    schemaVersion: 1,
    clientSubmissionId: attempt.id,
    studentRef: input.studentRef,
    assignmentRef: input.assignmentRef,
    questionRef: question.id,
    skillCode: question.skillCode,
    response: { type: 'integer', value: attempt.finalResponse },
    firstResponse: { type: 'integer', value: attempt.firstResponse },
    correctionCount: attempt.correctionCount,
    hintCount: attempt.hintCount,
    timing: attempt.duration,
    startedAt: attempt.startedAt,
    completedAt: attempt.completedAt,
    ...(attempt.ink ? {
      ink: {
        present: true as const,
        formatVersion: 1 as const,
        strokeCount: attempt.ink.strokes.length,
        pointCount: countInkPoints(attempt),
      },
    } : {}),
  };
}
